import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import Grid from '@mui/system/Unstable_Grid';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Stack,
  Drawer,
  Divider,
  MenuItem,
  CardHeader,
  Typography,
  CardActions,
  CardContent
} from '@mui/material';

import { useCustomFields } from 'src/hooks/use-custom-fields';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { EntityLabelType } from 'src/services/utils/utils.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetCountriesQuery, useSearchLabelsQuery } from 'src/services/utils/utils.service';
import { hideCompanyEditor, setLatestCreatedCompany } from 'src/store/slices/companies/companiesSlice';
import { useGetCompanyByIdQuery, useCreateLocalCompanyMutation, useUpdateLocalCompanyMutation } from 'src/services/connections/connections.service';

import ImageCropper from 'src/components/image-cropper/image-cropper';
import FormProvider, { RHFSelect, RHFTextField, RHFAutocomplete } from 'src/components/hook-form';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose?: () => void;
  profileId?: string | null;
}

export default function CompanyEditorSideSheet({ open, onClose = () => { }, profileId = null }: Props) {

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  // Editing a company has to load its existing values, otherwise saving replaces them with the
  // empty set the form is holding.
  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.COMPANY,
    entityId: profileId ?? undefined,
  });

  const dispatch = useAppDispatch();

  const [createCompanyProfile] = useCreateLocalCompanyMutation();
  const [updateCompanyProfile] = useUpdateLocalCompanyMutation();

  const { data: countries } = useGetCountriesQuery();

  const [photoURL, setPhotoURL] = useState('');
  const [labelSearchQuery, setLabelSearchQuery] = useState<string>('');

  // `currentData`, not `data`: RTK Query retains `data` from the previous argument when a hook
  // switches to `skip`, and this sheet stays mounted across close/reopen, so `data` would keep
  // serving the company that was last edited.
  const { currentData: profile } = useGetCompanyByIdQuery({ profile_id: profileId as string }, {
    skip: !profileId,
  });

  // The caller's intent decides the mode, never the cache contents. Deriving it from `profile`
  // would show create mode for a beat while an edit record is still loading, so a fast submit
  // would create a duplicate.
  const isEditMode = Boolean(profileId);

  const { currentData: availableLabels } = useSearchLabelsQuery({
    label: labelSearchQuery ?? undefined,
    type: EntityLabelType.COMPANY,
    page: 1,
    per_page: 10
  });

  const CompanySchema = Yup.object().shape({
    company_name: Yup.string().required(t('validation.required')),
    company_information: Yup.string().nullable(),
    company_website: Yup.string()
      .url(t('validation.url'))
      .nullable(),
    company_type: Yup.string().required(t('validation.required')),
    tax_id: Yup.string().nullable(),
    country_code: Yup.string().nullable(),
    company_logo_id: Yup.string().nullable(),
    labels: Yup.array().of(Yup.mixed<{ value: string, label: string }>()).nullable(),
  });

  const defaultValues = useMemo(() => ({
    company_name: '',
    company_information: '',
    company_website: '',
    company_type: 'client' as string,
    tax_id: '',
    country_code: '',
    company_logo_id: '',
    labels: [],
  }), []);

  const methods = useForm({
    resolver: yupResolver(CompanySchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting: formSubmitting },
  } = methods;

  // Authoritative for both branches, so the form self-heals on open even when the previous
  // session was closed by a path that bypasses `handleClose`.
  useEffect(() => {
    if (!open) return;

    if (isEditMode) {
      if (!profile) return; // wait for the record rather than blanking the form

      setPhotoURL(profile.company_logo?.public_path || '');

      reset({
        company_name: profile.company_name,
        company_information: profile.company_information || '',
        company_website: profile.company_website || '',
        company_type: profile.company_type as string,
        tax_id: profile.tax_id || '',
        country_code: profile.country_code || '',
        company_logo_id: profile.company_logo?.id || '',
        labels: Array.isArray(profile.labels) ? profile.labels.map((label) => ({
          label: label.label,
          value: label.id as string,
        })) : [],
      });
    } else {
      setPhotoURL('');
      customFields.resetFields();
      reset(defaultValues);
    }
    // `customFields.resetFields` is a stable callback; the `customFields` object itself is a new
    // reference every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode, profile, reset, defaultValues, customFields.resetFields]);

  const onSubmit = handleSubmit(async (data) => {
    try {

      // Validate custom fields first
      const customFieldValidation = customFields.validate();
      if (!customFieldValidation.isValid) {
        return;
      }

      const payload = {
        company_name: data.company_name,
        company_information: data.company_information as string,
        country_code: data.country_code as string,
        tax_id: data.tax_id as string,
        company_type: data.company_type as TenantType,
        company_website: data.company_website as string,
        company_logo_id: data.company_logo_id as string,
        labels: Array.isArray(data.labels) ? data.labels.map((item) => ({
          id: item?.value as string,
        })) : [],
        custom_fields: customFields.getData(),
      };

      if (isEditMode) {
        await updateCompanyProfile({
          profile_id: profileId as string,
          data: payload,
        }).unwrap();
      } else {
        const createdCompany = await createCompanyProfile(payload).unwrap();

        dispatch(setLatestCreatedCompany({
          id: createdCompany.id,
          company_name: createdCompany.company_name,
        }));
      }

      enqueueSnackbar(isEditMode ? t('companies.editor-sidesheet.api.update.success') : t('companies.editor-sidesheet.api.create.success'), { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar(t('companies.editor-sidesheet.api.error'), { variant: 'error' });
    }
  });

  const selectableLabels = useMemo(() => {
    if (!availableLabels?.results) return [];

    return availableLabels.results.map((label) => ({
      label: label.label as string,
      value: label.id as string,
    }));
  }, [availableLabels]);

  const handleClose = (e?: any, reason?: string) => {
    if (reason && reason === 'backdropClick') return;

    // `reset()` with no argument restores whatever the last `reset(values)` installed, because
    // RHF adopts those as the new defaults.
    reset(defaultValues);
    setPhotoURL('');
    customFields.resetFields();
    dispatch(hideCompanyEditor());
    onClose();
  }

  const onEditPhoto = (fileId: string) => {
    setValue('company_logo_id', fileId);
  };

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setLabelSearchQuery(value);
    }, 500),
    [setLabelSearchQuery]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 100 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, height: '100%', backgroundColor: 'white !important' } }}
    >
      <Box sx={{ px: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>

        <CardHeader
          title={isEditMode ? t('companies.editor-sidesheet.edit.title') : t('companies.editor-sidesheet.create.title')}
          subheader={t('companies.editor-sidesheet.form.subheader')}
          subheaderTypographyProps={{ variant: 'caption', mb: 2 }}
        />

        <Divider />
        <CardContent sx={{ flexGrow: 1, overflowY: 'auto', padding: 3 }}>
          <FormProvider methods={methods} onSubmit={onSubmit}>
            <Grid container gap={2}>

              <Grid xs={12}>
                <ImageCropper
                  label='Company Logo'
                  onSubmit={onEditPhoto}
                  existingUrl={photoURL}
                  showGrid
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="company_name"
                  label={t('companies.editor-sidesheet.form.company_name.label')}
                  placeholder={t('companies.editor-sidesheet.form.company_name.placeholder')}
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="company_information"
                  label={t('companies.editor-sidesheet.form.company_information.label')}
                  placeholder={t('companies.editor-sidesheet.form.company_information.placeholder')}
                  multiline
                  rows={4}
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="company_website"
                  label={t('companies.editor-sidesheet.form.company_website.label')}
                  placeholder={t('companies.editor-sidesheet.form.company_website.placeholder')}
                />
              </Grid>

              <Grid xs={12}>
                <RHFSelect
                  name="company_type"
                  label={t('companies.editor-sidesheet.form.company_type.label')}
                  placeholder={t('companies.editor-sidesheet.form.company_type.placeholder')}
                >
                  {[TenantType.Client, TenantType.Recruiter].map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`companies.editor-sidesheet.form.company_type.options.${type}`)}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="tax_id"
                  label={t('companies.editor-sidesheet.form.tax_id.label')}
                  placeholder={t('companies.editor-sidesheet.form.tax_id.placeholder')}
                />
              </Grid>

              <Grid xs={12}>
                <RHFSelect
                  name="country_code"
                  label={t('companies.editor-sidesheet.form.country_code.label')}
                  placeholder={t('companies.editor-sidesheet.form.country_code.placeholder')}
                >
                  {!countries && (
                    <MenuItem key="default" value={undefined}>
                      {' '}
                    </MenuItem>
                  )}
                  {countries?.map((country, index) => (
                    <MenuItem key={`country-${country.code}-${index}`} value={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </RHFSelect>

              </Grid>

              <Grid xs={12}>

                <RHFAutocomplete
                  name="labels"
                  multiple
                  label={t('companies.editor-sidesheet.form.company_labels.label')}
                  placeholder={t('companies.editor-sidesheet.form.company_labels.placeholder')}
                  options={selectableLabels}
                  renderOption={({ key, ...rest }, option: { label: string, value: string }) => (
                    <li {...rest} key={option.value}>
                      <Stack direction="row" justifyContent="space-between" width="100%">
                        <Typography variant="subtitle2">{option.label}</Typography>
                      </Stack>
                    </li>
                  )}
                  onInputChange={(_e, value) => onSearchQueryChange(value)}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                />
              </Grid>

              {customFields.hasCustomFields && (
                <>
                  {customFields.renderCustomFields}
                </>
              )}
            </Grid>

          </FormProvider>
        </CardContent>

        <CardActions >
          <Stack direction="row" spacing={2} sx={{ width: '100%', my: 2 }} justifyContent="flex-end">
            <LoadingButton
              variant="outlined"
              onClick={handleClose}
            >
              {t('common.cancel')}
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={onSubmit}
              loading={formSubmitting}
              disabled={isEditMode && !profile}
            >
              {isEditMode ? t('companies.editor-sidesheet.form.update') : t('companies.editor-sidesheet.form.create')}
            </LoadingButton>
          </Stack>
        </CardActions>
      </Box>
    </Drawer>
  );
}